import { z } from 'zod';
import { crmNormalizeTaxId } from './crm';
import { TaxIdValidator } from './taxid';

// Aba **Dados** do lead — cadastro do CNPJ vindo da API Kipflow.
//
// É irmã do dossiê (`crmCompanyDossier`), mas resolve outro problema e por isso
// tem contrato próprio. O dossiê é uma foto COMERCIAL montada de seis fontes,
// com validade curta e feita para ser refeita. Isto aqui é o CADASTRO: razão
// social, natureza jurídica, CNAE, porte, tributação, faturamento estimado e
// quadro societário. Muda uma vez por ano, se mudar.
//
// Consequência da diferença: **uma consulta por CNPJ, para sempre** (ordem do
// owner). Cada chamada custa R$ 0,29 e não há botão de atualizar. A barreira é o
// servidor, não a UI — ver `crmFetchCompanyData`.

/**
 * Sócio do quadro societário. O `documento` chega MASCARADO da Kipflow
 * (`***400459**`) e é gravado como veio: mascarar de novo não acrescenta nada, e
 * "desmascarar" não é possível nem desejável.
 */
export const CrmCompanyPartnerSchema = z.object({
  nome: z.string().nullable(),
  documento: z.string().nullable(),
  qualificacao: z.string().nullable(),
  dataEntrada: z.string().nullable(),
  faixaEtaria: z.string().nullable(),
  sexo: z.string().nullable(),
  /** `PESSOA FISICA` / `PESSOA JURIDICA` — separa sócio pessoa de holding. */
  identificador: z.string().nullable(),
});
export type CrmCompanyPartner = z.infer<typeof CrmCompanyPartnerSchema>;

/**
 * `NOT_FOUND` também é resultado GRAVADO: a consulta sem match é cobrada igual, e
 * o mesmo CNPJ devolveria o mesmo nada. Guardar o "não achamos" é o que impede a
 * segunda cobrança.
 */
export const CrmCompanyDataStatusSchema = z.enum(['OK', 'NOT_FOUND']);
export type CrmCompanyDataStatus = z.infer<typeof CrmCompanyDataStatusSchema>;

export const CrmCompanyDataSchema = z.object({
  status: CrmCompanyDataStatusSchema,
  /**
   * O CNPJ que foi CONSULTADO (14 dígitos), não o que o lead tem hoje. É a chave
   * da idempotência: o servidor só devolve o salvo enquanto o lead continuar
   * apontando para este documento. Corrigir o CNPJ do lead é consulta nova, não
   * repetição da mesma.
   */
  cnpj: z.string(),
  fetchedAt: z.number().int().nonnegative(),
  fetchedBy: z.string(),
  /**
   * Custo cobrado pela consulta, em BRL e como o fornecedor mandou. Vai para a
   * TELA (no cabeçalho da aba) e para o acumulado do dia do usuário — em cents,
   * via `crmLookupCostCents`. O resto do `pricing` continua interno da Kipflow.
   *
   * `null` quando a resposta não informou: nesse caso a aba omite o valor em vez
   * de escrever "R$ 0,00", e o acumulado do dia não soma nada.
   */
  cost: z.number().nullable(),

  razaoSocial: z.string().nullable(),
  nomeFantasia: z.string().nullable(),
  situacaoCadastral: z.string().nullable(),
  naturezaJuridica: z.string().nullable(),
  porte: z.string().nullable(),
  /** Em unidades de BRL (não cents — é como a Receita publica). */
  capitalSocial: z.number().nullable(),
  cnaePrincipal: z.string().nullable(),
  cnaePrincipalNome: z.string().nullable(),
  ramoAtividade: z.string().nullable(),
  segmento: z.string().nullable(),
  nivelAtividade: z.string().nullable(),
  formaTributacao: z.string().nullable(),
  // Os três abaixo chegam da API ora como boolean, ora como string ("SIM"/"NAO",
  // "PRIVADA"), e o contrato não promete qual. Guardamos o texto COMO VEIO em vez
  // de inferir: um `false` errado numa tela comercial vale menos que um rótulo
  // cru que o vendedor lê e entende.
  opcaoSimples: z.string().nullable(),
  opcaoMei: z.string().nullable(),
  matriz: z.string().nullable(),
  faturamento: z.number().nullable(),
  faixaFaturamento: z.string().nullable(),
  faixaFuncionarios: z.string().nullable(),
  qtdeFiliais: z.number().nullable(),
  dataInicioAtividade: z.string().nullable(),
  socios: z.array(CrmCompanyPartnerSchema).default([]),
});
export type CrmCompanyData = z.infer<typeof CrmCompanyDataSchema>;

/**
 * Id do doc em `crm_leads/{leadId}/crm_research`: **um por CNPJ consultado**,
 * não um por lead.
 *
 * A diferença é o que faz "uma consulta por CNPJ, para sempre" ser verdade. Com
 * doc único por lead, corrigir um CNPJ errado e depois voltar ao original
 * pagaria de novo pelo mesmo documento (o doc teria sido sobrescrito), e a aba
 * exibiria a empresa do CNPJ antigo até alguém consultar de novo. Com o CNPJ no
 * ID, o lead sempre lê o doc DAQUELE CNPJ: se nunca foi consultado, a aba mostra
 * o botão; se já foi, mostra os dados — sem cobrar.
 *
 * Divide a subcoleção com o dossiê (`latest`) de propósito: as rules dela já
 * negam escrita do cliente e liberam leitura conforme a visibilidade do lead —
 * exatamente o que um dado pago precisa.
 */
export function crmCompanyDataDocId(cnpj: string): string {
  return `kipflow_${cnpj}`;
}

/**
 * Custo de uma consulta, em BRL. Exibido antes do clique — o vendedor sabe o que
 * gasta.
 *
 * É também o valor gravado quando a Receita não conhece o CNPJ (404): a consulta
 * sem match é cobrada igual (#209) e já debita o teto diário, então deixar o
 * custo em `null` faria a mesma tela dizer "consumiu uma consulta" e "custou
 * R$ 0,00". É o preço de TABELA, não um recibo do fornecedor — se um dia a
 * Kipflow informar o custo no 404, é ele que deve prevalecer.
 */
export const CRM_COMPANY_DATA_COST_BRL = 0.29;

export const CrmFetchCompanyDataInputSchema = z.object({
  boardId: z.string().min(1),
  leadId: z.string().min(1),
});
export type CrmFetchCompanyDataInput = z.infer<typeof CrmFetchCompanyDataInputSchema>;

export type CrmFetchCompanyDataResult = {
  data: CrmCompanyData;
  /** `true` quando o documento já estava salvo e nada foi consultado (nem cobrado). */
  cached: boolean;
};

/**
 * CNPJ consultável do lead — 14 dígitos — ou `null`.
 *
 * A MESMA função decide o botão na UI e a recusa no servidor. Duas cópias
 * divergindo dariam o pior dos mundos: botão habilitado que o servidor rejeita,
 * ou botão morto sem explicação (é a lição do `crmMissingResearchFields`).
 *
 * `countryCode` ausente conta como BR: leads antigos e importados da Base CNPJ
 * nasceram sem país e têm CNPJ legítimo. País preenchido e diferente de BR é
 * `null` — a Kipflow só conhece a Receita brasileira.
 */
export function crmLeadCnpj(lead: {
  taxId?: string | null | undefined;
  countryCode?: string | null | undefined;
}): string | null {
  const country = (lead.countryCode ?? '').trim().toUpperCase();
  if (country !== '' && country !== 'BR') return null;
  // Exigimos 14 DÍGITOS depois da normalização, não "14 dígitos em algum lugar":
  // um `taxId` digitado errado (com letra no meio) viraria uma consulta paga que
  // já nasce condenada. Pontuação some no `crmNormalizeTaxId`; letra não.
  const normalized = crmNormalizeTaxId(lead.taxId ?? '');
  if (!/^\d{14}$/.test(normalized)) return null;
  // E o dígito verificador: um typo que preserve 14 dígitos passaria no teste
  // acima e viraria R$ 0,29 gastos num CNPJ que não existe — resultado que fica
  // gravado, porque "não encontrado" também é cache. O mod-11 é de graça.
  return TaxIdValidator.specFor('BR').validate(normalized) == null ? normalized : null;
}
